// @flow
import * as React from 'react';
import { graphql, compose } from 'react-apollo';

import adminDistrictSchoolAdminsViewQuery from 'admin/queries/adminDistrictSchoolAdminsViewQuery.gql';
import adminOrgAdminsViewQuery from 'admin/queries/adminOrgAdminsViewQuery.gql';
import ADD_ADMIN_MUTATION from 'admin/queries/AddAdminMutation.gql';
import DIRECT_ADD_ADMIN_MUTATION from 'admin/queries/DirectAddAdminMutation.gql';

import type { addAdmin } from 'admin/queries/__generated__/addAdmin';
import type { directAddAdmin } from 'admin/queries/__generated__/directAddAdmin';

type Props = {
  children: Function,
  addAdmin: (
    organizationId: ?Backbone$Id,
    userUuid: ?string,
    districtId: ?Backbone$Id
  ) => Promise<{ data: addAdmin }>,
  directAddAdmin: (
    organizationId: ?Backbone$Id,
    name: ?string,
    email: ?string,
    districtId: ?number
  ) => Promise<{ data: directAddAdmin }>,
};

class AdminAddAdminContainer extends React.Component<Props, void> {
  render() {
    return this.props.children({
      addAdmin: this.props.addAdmin,
      directAddAdmin: this.props.directAddAdmin,
    });
  }
}

const updateAdminCache = (
  store: Object,
  admin: Object,
  applicableQuery: Object,
  organizationId: number
) => {
  const currentData = store.readQuery(applicableQuery);
  let targetOrg;

  if (applicableQuery.query === adminDistrictSchoolAdminsViewQuery) {
    targetOrg = currentData.organizations[0].childOrganizations.find(
      org => org.id === parseInt(organizationId, 10)
    );
  } else {
    [targetOrg] = currentData.organizations;
  }

  targetOrg.admins.push(admin);

  store.writeQuery({ ...applicableQuery, data: currentData });
};

const makeAdminFromAdminVerification = (adminVerification: *) => {
  return {
    id: adminVerification.user.id,
    uuid: adminVerification.user.uuid,
    name: adminVerification.user.name,
    firstName: adminVerification.user.first_name,
    lastName: adminVerification.user.last_name,
    __typename: 'OrgAdminDescription',
  };
};

const withAddAdmin = graphql(ADD_ADMIN_MUTATION, {
  props: ({ mutate }) => ({
    addAdmin(organizationId: number, userUuid: ?string, districtId: ?number) {
      const input = { organizationId, userUuid };
      const applicableQuery = districtId
        ? {
            query: adminDistrictSchoolAdminsViewQuery,
            variables: { organizationId: districtId },
          }
        : { query: adminOrgAdminsViewQuery, variables: { organizationId } };
      return mutate({
        variables: { input },
        update: (
          store,
          {
            data: {
              addAdmin: { admin },
            },
          }
        ) => {
          if (admin && admin.adminState !== 'pending') {
            // Only update the cache if the admin is not pending
            // otherwise, the user will refresh the page and the admin will disappear
            updateAdminCache(
              store,
              makeAdminFromAdminVerification(admin),
              applicableQuery,
              organizationId
            );
          }
        },
      });
    },
  }),
});

const withDirectAddAdmin = graphql(DIRECT_ADD_ADMIN_MUTATION, {
  props: ({ mutate }) => ({
    directAddAdmin(organizationId: number, name: ?string, email: ?string, districtId: ?number) {
      const input = { organizationId, name, email };
      const applicableQuery = districtId
        ? {
            query: adminDistrictSchoolAdminsViewQuery,
            variables: { organizationId: districtId },
          }
        : { query: adminOrgAdminsViewQuery, variables: { organizationId } };
      return mutate({
        variables: { input },
        update: (
          store,
          {
            data: {
              directAddAdmin: { admin },
            },
          }
        ) => {
          if (admin && admin.adminState !== 'pending') {
            // Only update the cache if the admin is not pending
            // otherwise, the user will refresh the page and the admin will disappear
            updateAdminCache(
              store,
              makeAdminFromAdminVerification(admin),
              applicableQuery,
              organizationId
            );
          }
        },
      });
    },
  }),
});

export { AdminAddAdminContainer };

export default compose(withAddAdmin, withDirectAddAdmin)(AdminAddAdminContainer);
